/**
 * GenericItemsSection — a responsive card grid with optional header.
 *
 * Props:
 *   eyebrow    string
 *   heading    string
 *   subheading string
 *   alignment  'center'|'left'|'right'   default 'center'
 *   items      Array<{ id, title, text, imageUrl, href }>
 *
 * Cards with neither title nor text are skipped silently.
 * Renders nothing when items is empty.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Card, CardActionArea, CardContent, CardMedia, Container, Grid, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ArrowForward } from '@mui/icons-material';
import SectionContainer from '../../components/SectionContainer';
import RevealOnScroll from '../../components/RevealOnScroll';

function ItemCard({ item }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const hasContent = item.title || item.text;
  if (!hasContent && !item.imageUrl) return null;

  const cardContent = (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'background.paper',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 3,
        transition: 'transform 0.25s ease, box-shadow 0.25s ease',
        '&:hover': {
          transform: item.href ? 'translateY(-4px)' : undefined,
          boxShadow: item.href
            ? isDark
              ? '0 12px 32px rgba(0,0,0,0.35)'
              : '0 12px 32px rgba(0,0,0,0.1)'
            : undefined,
        },
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {item.imageUrl && (
        <CardMedia
          component="img"
          image={item.imageUrl}
          alt={item.title || ''}
          sx={{
            height: 200,
            objectFit: 'cover',
            borderRadius: '12px 12px 0 0',
          }}
          loading="lazy"
          decoding="async"
        />
      )}
      <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
        {item.title && (
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: '1rem',
              color: 'text.primary',
              mb: item.text ? 1 : 0,
              lineHeight: 1.35,
            }}
          >
            {item.title}
          </Typography>
        )}
        {item.text && (
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              lineHeight: 1.65,
              whiteSpace: 'pre-line',
            }}
          >
            {item.text}
          </Typography>
        )}
        {item.href && (
          <Box sx={{ mt: 2 }}>
            <Button
              size="small"
              endIcon={<ArrowForward sx={{ fontSize: 16 }} />}
              sx={{ textTransform: 'none', fontWeight: 600, p: 0 }}
              tabIndex={-1}
            >
              Learn more
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  if (item.href) {
    return (
      <Box
        component="a"
        href={item.href}
        sx={{
          display: 'flex',
          height: '100%',
          textDecoration: 'none',
          color: 'inherit',
        }}
        aria-label={item.title || 'View more'}
      >
        {cardContent}
      </Box>
    );
  }

  return cardContent;
}

ItemCard.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    text: PropTypes.string,
    imageUrl: PropTypes.string,
    href: PropTypes.string,
  }).isRequired,
};

export default function GenericItemsSection({ eyebrow, heading, subheading, alignment, items }) {
  const safeItems = Array.isArray(items) ? items.filter(i => i.title || i.text || i.imageUrl) : [];

  if (safeItems.length === 0 && !eyebrow && !heading && !subheading) return null;

  // SectionContainer centers its header; we respect `alignment` for the grid
  const textAlign = alignment === 'left' ? 'left' : alignment === 'right' ? 'right' : 'center';

  return (
    <SectionContainer
      eyebrow={eyebrow}
      title={heading}
      subtitle={subheading}
      sx={{
        // Override SectionContainer's centered header when alignment is not center
        '& > .MuiContainer-root > :first-child': {
          textAlign,
        },
      }}
    >
      {safeItems.length > 0 && (
        <Grid container spacing={3}>
          {safeItems.map((item, idx) => (
            <Grid item xs={12} sm={6} md={4} key={item.id || idx}>
              <RevealOnScroll delay={Math.min(idx * 0.07, 0.42)}>
                <Box sx={{ height: '100%' }}>
                  <ItemCard item={item} />
                </Box>
              </RevealOnScroll>
            </Grid>
          ))}
        </Grid>
      )}
    </SectionContainer>
  );
}

GenericItemsSection.propTypes = {
  eyebrow: PropTypes.string,
  heading: PropTypes.string,
  subheading: PropTypes.string,
  alignment: PropTypes.oneOf(['left', 'center', 'right']),
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string,
      text: PropTypes.string,
      imageUrl: PropTypes.string,
      href: PropTypes.string,
    })
  ),
};

GenericItemsSection.defaultProps = {
  alignment: 'center',
  items: [],
};
